import { useEffect, useRef } from 'react';

import { useLocation } from 'react-router-dom';
import observer from 'saddlebag-observer';

import DetailedFullMap from './DetailedFullMap';
import DetailsPageGallery from './DetailsPageGallery';
import PageContent from './PageContent';

import type { DetailsPageContextProps as Props } from './types';
import type { PriceRange } from '@shared/types/frontend/ubff/hotel-detail-price';
import type { Nullable, Stay } from '@shared/types/utils';

import HotelImagesProvider from '@hotels-hotel-detail/data-access/HotelImagesProvider';
import HotelOffersProvider from '@hotels-hotel-detail/data-access/HotelOffersProvider';
import PoiDataProvider from '@hotels-hotel-detail/data-access/PoiDataProvider';
import { transformFiltersUrlParam } from '@hotels-hotel-detail/utils/hotel-offers';
import {
  HASH_FULL_MAP,
  HASH_HOTELS_GALLERY,
} from '@shared/constants/hash-routes';
import useSaveToListInteraction from '@shared/data-access-frontend/hooks/useSaveToListInteraction';
import HashRoute from '@shared/ui/common/HashRoute';
import ErrorBoundary from '@shared/ui/components/ErrorBoundary';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import {
  HOTEL_PRICES_ANCHOR,
  NAVIGATION_ITEMS,
  scrollTo,
  scrollToElement,
} from '@shared/utils-frontend/navigation-service';
import {
  updateSearchParamsUrl,
  verifyDetailsUrl,
  verifySearchUrl,
} from '@shared/utils-frontend/url/url';
import {
  isFromHPA,
  SEO_TRAFFIC_SOURCE,
  trafficSource,
} from '@shared/utils-universal/traffic-source';

export const parseFiltersParam = (filtersStr: string | null) => {
  if (!filtersStr) {
    return null;
  }

  try {
    return JSON.parse(filtersStr);
  } catch (e) {
    return null;
  }
};
// Exported for testing purposes only.
export const parsePriceFiltersParam = (
  priceFiltersStr: string | null,
): PriceRange[] => {
  if (!priceFiltersStr) {
    return [];
  }

  try {
    const parsed = JSON.parse(priceFiltersStr);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(
      (range) =>
        range &&
        typeof range === 'object' &&
        (typeof range.minPrice === 'number' ||
          typeof range.maxPrice === 'number'),
    );
  } catch {
    return [];
  }
};

const goBackToDV = (event: PopStateEvent) => {
  if (!event.state?.back) {
    return;
  }

  if (window.opener) {
    window.close();
  }

  // WORKAROUND: We can't directly check user can go back or not. Check the previous href to judge if it has redirected.
  setTimeout(() => {
    if (verifyDetailsUrl(window.location.pathname)) {
      window.location.href = window.document.referrer;
    }
  }, 500);
};

const HotelDetailsPage = (props: Props) => {
  const { configs, i18n, stay, userContext } = props;

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const searchCycleId = searchParams.get('search_cycle_id');
  const initialPriceType = searchParams.get('priceType');
  const dayviewCorrelationId = searchParams.get('correlation_id');
  const needScroll = searchParams.get('need_scroll');
  const searchAnchorId = searchParams.get('anchor');
  const sourceFromUrl = searchParams.get('source');
  const filtersParam = searchParams.get('filters');
  const priceFiltersParam = searchParams.get('price_filters');

  const source = trafficSource({
    source: sourceFromUrl,
  });

  const initialFilters = transformFiltersUrlParam(
    parseFiltersParam(filtersParam),
  );

  const priceRanges = parsePriceFiltersParam(priceFiltersParam);

  const isFromHpa = isFromHPA(source);

  const scrolledByAnchorRef = useRef(false);
  const priceInitializedRef = useRef(false);
  const reviewsInitializedRef = useRef(false);
  const scrollToDealsRef = useRef(true);
  const currentStayRef = useRef(stay);

  const setSearchCycleIdHandlerRef = useRef<Nullable<[string, Function]>>(null);
  const hotelDataLoadedHandlerRef = useRef<Nullable<[string, Function]>>(null);

  useSaveToListInteraction({ userContext, i18n, configs });

  const onPageDataReady = ({ type }: { type: string }) => {
    if (type === 'prices') {
      priceInitializedRef.current = true;
    } else if (type === 'reviews') {
      reviewsInitializedRef.current = true;
    }

    if (
      priceInitializedRef.current &&
      reviewsInitializedRef.current &&
      searchAnchorId &&
      !scrolledByAnchorRef.current
    ) {
      const navItem = NAVIGATION_ITEMS.find(
        (item) => item.id === searchAnchorId,
      );
      if (navItem) {
        scrollTo(document.getElementById(navItem.anchorName), 'smooth');
        scrolledByAnchorRef.current = true;
      }
    }
  };

  const shouldEnableBackBtn = () => {
    const { browserName } = props.userContext.device;
    const isChromeBrowser = browserName?.toLowerCase() === 'chrome';

    const isReferredFromMe = window.document.referrer.startsWith(
      window.location.origin,
    );
    const verifiedPathname =
      verifySearchUrl(window.document.referrer) &&
      verifyDetailsUrl(window.location.href);

    return isChromeBrowser && isReferredFromMe && verifiedPathname;
  };

  const onStayChange = (newStay: Stay) => {
    currentStayRef.current = newStay;
  };

  useEffect(() => {
    const {
      i18n: { formatDate },
    } = props;

    const setupComponent = () => {
      // In order to create new searchCycleId when user change market or currency or Language in header, we set searchCycleId as null here.
      setSearchCycleIdHandlerRef.current = observer.subscribe(
        'culture-button-clicked',
        () => {
          const updatedData = {
            stay: currentStayRef.current,
            source: props.isSeoTraffic ? SEO_TRAFFIC_SOURCE : source,
          };
          updateSearchParamsUrl(updatedData, formatDate);
        },
      );

      hotelDataLoadedHandlerRef.current = observer.subscribe(
        'hotel-data-result-is-loaded',
        onPageDataReady,
      );

      if (scrollToDealsRef.current && needScroll) {
        scrollToElement(HOTEL_PRICES_ANCHOR, 160);
        scrollToDealsRef.current = false;
      }

      if (shouldEnableBackBtn()) {
        window.history.replaceState({ back: true }, '', window.location.href);
        window.history.pushState({}, '', window.location.href); // make back button clickable for new tab in chrome
        window.addEventListener('popstate', (event) => goBackToDV(event));
      }
    };

    setupComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      observer.unsubscribe(setSearchCycleIdHandlerRef.current);
      observer.unsubscribe(hotelDataLoadedHandlerRef.current);

      if (shouldEnableBackBtn()) {
        window.removeEventListener('popstate', goBackToDV);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <HotelOffersProvider
      i18n={props.i18n}
      hotelId={props.uBFFHotel.hotelId}
      entityId={props.searchEntityId || undefined}
      searchId={searchCycleId || undefined}
      stay={props.stay}
      dayViewCorrelationId={dayviewCorrelationId || undefined}
      isSeoTraffic={props.isSeoTraffic}
      initialPriceType={initialPriceType}
      initialFilters={initialFilters}
      priceRanges={priceRanges}
      userContext={props.userContext}
    >
      <HotelImagesProvider
        translate={props.i18n.translate}
        hotelId={props.uBFFHotel.hotelId}
        locale={props.i18n.culture.locale}
        currency={props.i18n.culture.currency}
        market={props.i18n.culture.market}
        gallery={props.uBFFHotel.gallery}
        reviewImages={props.uBFFHotel.reviewImages}
        imageCount={props.uBFFHotel.imageCount}
      >
        <PoiDataProvider
          uBFFHotel={props.uBFFHotel}
          searchEntityId={props.searchEntityId}
        >
          <PageContent
            onStayChange={onStayChange}
            isFromHpa={isFromHpa}
            {...props}
            stay={currentStayRef.current}
          />
          <ErrorBoundary componentName="DetailedFullMap">
            <HashRoute
              hash={HASH_FULL_MAP}
              render={({ onClose }) => (
                <DetailedFullMap
                  uBFFHotel={props.uBFFHotel}
                  onClose={onClose}
                />
              )}
            />
          </ErrorBoundary>
          <HashRoute
            hash={HASH_HOTELS_GALLERY}
            render={({ onClose }) => <DetailsPageGallery onClose={onClose} />}
          />
        </PoiDataProvider>
      </HotelImagesProvider>
    </HotelOffersProvider>
  );
};

export default withConfiguration(withUserContext(withI18n(HotelDetailsPage)));
